/**
 * Shared ops inbox taxonomy (frontend) — mirrors backend/src/notifications/taxonomy.js
 */

export const INBOX_TABS = [
  { id: "all", label: "All" },
  { id: "action_required", label: "Action Required" },
  { id: "leads", label: "Leads" },
  { id: "quotes", label: "Quotes" },
  { id: "payments", label: "Payments" },
  { id: "operations", label: "Operations" },
  { id: "system", label: "System" },
];

export const ACTION_REQUIRED_TYPES = [
  "call_requested",
  "call_request_assigned",
  "call_request_sla_breached",
  "call_request_failed",
  "followup_due",
  "followup_overdue",
  "followup_reminder",
  "quote_update_requested",
  "agent_requested",
  "quote_expired",
  "quote_expiring_soon",
  "technical_error",
  "quote_processing_error",
  "agent_connection_error",
  "payment_processing_error",
  "lead_creation_error",
  "conversation_error",
  "validation_error",
  "database_error",
  "external_api_error",
];

export const LEAD_TYPES = [
  "lead_created",
  "lead_updated",
  "lead_status_changed",
  "submission_resubmission",
  "submission_group_created",
  "lead_creation_error",
];

export const QUOTE_TYPES = [
  "quote_created",
  "quote_status_changed",
  "quote_expired",
  "quote_expiring_soon",
  "quote_update_requested",
  "quote_update_accepted",
  "quote_update_rejected",
  "quote_processing_error",
];

export const OPERATIONS_ENTITY_TYPES = [
  "order",
  "driver",
  "vehicle",
  "followup",
  "call_request",
];

export const isPaymentNotification = (notification) => {
  if (notification?.metadata?.proofType) return true;
  if (notification?.type === "payment_processing_error") return true;
  return /payment proof/i.test(notification?.title || "");
};

export const resolveNotificationCategory = (notification) => {
  if (isPaymentNotification(notification)) return "payments";

  const entityType = notification?.entityType;
  const type = notification?.type;

  if (entityType === "lead" || LEAD_TYPES.includes(type)) return "leads";
  if (entityType === "quote" || QUOTE_TYPES.includes(type)) return "quotes";
  if (OPERATIONS_ENTITY_TYPES.includes(entityType)) return "operations";
  return "system";
};

export const isActionRequired = (notification) => {
  if (notification?.isRead) return false;
  if (["high", "urgent"].includes(notification?.priority)) return true;
  if (ACTION_REQUIRED_TYPES.includes(notification?.type)) return true;
  return isPaymentNotification(notification);
};

export const getTypeLabel = (type = "") =>
  type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

export const getCtaLabel = (notification) => {
  if (isPaymentNotification(notification)) return "Review payment";
  switch (notification?.entityType) {
    case "lead":
      return "View lead";
    case "quote":
      return "View quote";
    case "order":
      return "View order";
    case "feedback":
      return "View feedback";
    case "call_request":
      return "Open call request";
    case "followup":
      return "Open follow-up";
    case "driver":
      return "View drivers";
    case "vehicle":
      return "View vehicles";
    case "agent_conversation":
      return "Open agent";
    default:
      return "Open";
  }
};

export const getNotificationPath = (notification) => {
  const id = notification?.entityId;
  if (!id) return null;

  switch (notification?.entityType) {
    case "lead":
      return `/leads/${id}`;
    case "quote":
      return `/quotes/${id}`;
    case "order":
      return `/orders?view=${id}`;
    case "feedback":
      return `/feedback?view=${id}`;
    case "call_request":
      return `/call-requests`;
    case "followup":
      return `/followups/${id}`;
    case "driver":
      return `/drivers`;
    case "vehicle":
      return `/vehicles`;
    case "agent_conversation":
      return `/agent`;
    default:
      return null;
  }
};

export const CATEGORY_ACCENT = {
  action_required: "#F3B431",
  leads: "#5BC0DE",
  quotes: "#23897A",
  payments: "#D9534F",
  operations: "#5CB85C",
  system: "#7B68A6",
  updates: "#5BC0DE",
  all: "#666666",
};
